import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

import api from '../../../services/api';
import { extractListData } from '../../../utils/Utils';

// Faz D (spec 2026-08-08 §3/§4, Task 4) — 1:1 Görüşmelerim FE veri katmanı.
// Tek kaynak: GET v1/one-on-ones/my (task-1-report.md §1) — DÜZ dizi
// (PaginatedList DEĞİL), her satırda myRole (manager|employee) ile ayrışan
// TEK liste. Ayrı bir GetById ucu YOK — liste + detay AYNI `data` dizisinden
// beslenir (id ile bulunur). Bu yüzden her mutasyon (create/update/addItem/
// updateItem/deleteItem) BE'den dönen güncel nesneyi `data` içine YERİNDE
// birleştirir — comp-cycles'ın ayrı bir GET /{id} ile detayı tazelediği
// desenin AKSİNE (task-1-report.md §4: burada öyle bir uç yok).

export const ONE_ON_ONE_STATUS_COLORS = {
  scheduled: { color: '#0291EF', bg: '#EFF6FF' },
  completed: { color: '#16A34A', bg: '#DCFCE7' },
  cancelled: { color: '#E53935', bg: '#FEF2F2' },
};

export const ONE_ON_ONE_ROLE_COLORS = {
  manager: { color: '#7C3AED', bg: '#F3E8FF' },
  employee: { color: '#0F766E', bg: '#CCFBF1' },
};

// comp-cycles/config'teki StatusBadge ile AYNI imza (color/bg düz prop, $ ön
// eki yok) — hem rol hem durum rozeti için TEK bileşen (rozet görsel dili
// birebir aynı, yalnız renk haritası değişiyor).
export const Badge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${(p) => p.bg};
  color: ${(p) => p.color};
`;

// Çalışan seçici — continuous-feedback/config.buildEmployeeAsync'in BAĞIMSIZ
// kopyası (repo konvansiyonu: farklı sayfa grubu = bağımsız kopya, bkz. o
// dosyanın kendi yorumu). GENEL /v1/employees ucu kullanılır — doğrudan-
// bağlılara ÖZEL bir uç YOK (task-1-report.md §"Direct-report çözüm
// deseni": "my-team benzeri bir uç Task 1 raporunda yok"). Seçici herhangi
// bir çalışanı listeler; hedef doğrudan rapor değilse POST 403
// (notDirectManager) döner — bu hata sayfada YAKALANMAZ, Axios interceptor'ı
// genel toast'la gösterir (task-4-report.md'de karar olarak not edildi).
export function buildEmployeeAsync() {
  return {
    onSearch: async (q) => {
      const params = q ? { 'fullName[like]': q, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
      const items = extractListData(res);
      return items.map((e) => ({
        id: e.id,
        name: e.fullName || `${e.firstName || ''} ${e.lastName || ''}`.trim(),
        profilePicture: e.profilePicture,
      }));
    },
    onGetById: async (id) => {
      if (!id) return null;
      const res = await api.get(`/v1/employees/${id}`);
      const e = res.data?.data;
      return e ? { id: e.id, name: e.fullName || `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture } : null;
    },
  };
}

export const employeeAsync = buildEmployeeAsync();

// --- Liste + detay: tek kaynaktan CRUD -------------------------------------

// Fix round 1 (coordinator review) — GET my artık düz dizi DEĞİL, zarf:
// { meetings: [...], canCreate: bool } (OneOnOneMyResponseDto, backend
// task-4-report.md "Fix round 1"). `canCreate` meeting listesinden BAĞIMSIZ
// (çağıranın en az bir aktif doğrudan bağlısı var mı) — boş `meetings`'te
// dahi doğru sinyal verir, "Yeni Görüşme" artık liste doluluğundan TÜRETİLMEZ.
export const useOneOnOnesApi = () => {
  const [data, setData] = useState([]);
  const [canCreate, setCanCreate] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    setFetching(true);
    api.get('/v1/one-on-ones/my')
      .then((res) => {
        const envelope = res.data?.data;
        setData(Array.isArray(envelope?.meetings) ? envelope.meetings : []);
        setCanCreate(!!envelope?.canCreate);
      })
      .finally(() => setFetching(false));
  }, [refreshTrigger]);

  // POST body { employeeId, scheduledAt } — yanıt TAM OneOnOneMeetingDto
  // (items dahil). Create gövdesi item KABUL ETMEZ, bu yüzden dönen items
  // varsa TAMAMEN carry-over'dır (task-1-report.md §6) — çağıran taraf
  // (OneOnOnesPage) bunu carry-over bandı için kullanır.
  const createMeeting = useCallback(async (payload) => {
    try {
      const res = await api.post('/v1/one-on-ones', payload);
      const meeting = res.data?.data;
      if (meeting) setData((prev) => [meeting, ...prev]);
      return meeting || null;
    } catch {
      return null;
    }
  }, []);

  // PUT body'nin üç alanı da BAĞIMSIZ/opsiyonel (task-1-report.md) — çağıran
  // yalnız değişen alan(lar)ı gönderir (status VEYA sharedNotes VEYA
  // myPrivateNote — asla karşı tarafın alanına dokunmaz, o alan DTO'da
  // zaten hiç yok). Yanıt TAM OneOnOneMeetingDto (items dahil).
  const updateMeeting = useCallback(async (id, patch) => {
    try {
      const res = await api.put(`/v1/one-on-ones/${id}`, patch);
      const meeting = res.data?.data;
      if (meeting) setData((prev) => prev.map((m) => (m.id === id ? meeting : m)));
      return meeting || null;
    } catch {
      return null;
    }
  }, []);

  const addItem = useCallback(async (meetingId, payload) => {
    try {
      const res = await api.post(`/v1/one-on-ones/${meetingId}/items`, payload);
      const item = res.data?.data;
      if (item) {
        setData((prev) => prev.map((m) => (m.id === meetingId
          ? { ...m, items: [...m.items, item] }
          : m)));
      }
      return item || null;
    } catch {
      return null;
    }
  }, []);

  const updateItem = useCallback(async (meetingId, itemId, patch) => {
    try {
      const res = await api.put(`/v1/one-on-ones/${meetingId}/items/${itemId}`, patch);
      const item = res.data?.data;
      if (item) {
        setData((prev) => prev.map((m) => (m.id === meetingId
          ? { ...m, items: m.items.map((it) => (it.id === itemId ? item : it)) }
          : m)));
      }
      return item || null;
    } catch {
      return null;
    }
  }, []);

  // Soft delete — yanıt Data taşımaz (task-1-report.md), bu yüzden yerel
  // state'ten doğrudan çıkarılır (başarı = silindi).
  const deleteItem = useCallback(async (meetingId, itemId) => {
    try {
      await api.delete(`/v1/one-on-ones/${meetingId}/items/${itemId}`);
      setData((prev) => prev.map((m) => (m.id === meetingId
        ? { ...m, items: m.items.filter((it) => it.id !== itemId) }
        : m)));
      return true;
    } catch {
      return false;
    }
  }, []);

  return {
    data, canCreate, fetching, refresh, createMeeting, updateMeeting, addItem, updateItem, deleteItem,
  };
};
